import { useEffect, useMemo, useState } from 'react'
import { Abrufausfall, useAbruf, useHintergrundabruf } from './Abruf'
import type { Abruf } from './Abruf'
import { api, imHintergrund } from './api'
import { fachlicherSatz } from './Fehlersatz'
import { meldeErfolg } from './Erfolgsstelle'
import { Auswahlfeld, ausStammdatum, mitCode } from './Auswahlfeld'
import { ohneStatistikkonten, ohneTechnische } from './Festkonten'
import type { Beschriftung } from './typen'
import { anwendername, beschriftung, bewegungsartname, gesellschaftsname, kontobezeichnung, periodenname, t, anzahlwort, zeitpunkt } from './sprache'
import { Periodenfeld } from './Periodenfeld'
import { laufenderMonat } from './Stilllegung'
import { Spaltenmassleiste, useSpaltenmasse } from './Spaltenbreiten'
import { Blattabschnitt } from './Stammdaten'
import { Zeilenmenue } from './Zeilenmenue'
import { Menueknopf, menueort } from './Aufrisszeilen'
import { Dialogfenster } from './Dialogfenster'
import { useDarf } from './Rechte'
import { useGemerkt } from './Gemerkt'
import type {
  Katalogeintrag,
  Kontenmapping,
  Bewegungsartmapping,
  VerwendeteZuordnungen,
  Wertzuordnung,
  Zielfeld,
} from './typen'

/** Solange die Zuordnungen nicht da sind, keine Zeile — was am Ort steht, sagt `leerstelle` (R1210). */
const KEINE_ARTZUORDNUNGEN: Bewegungsartmapping[] = []
const KEINE_WERTZUORDNUNGEN: Wertzuordnung[] = []
/** Ersatz, wenn der Vorrat der Zielfelder im Hintergrund nicht kam (R1212): keine Wahl. */
const KEINE_ZIELFELDER: Zielfeld[] = []

/** Was eine leere Tabelle von einem Abruf wissen muss (R1210). */
type Teilabruf = Pick<Abruf<unknown>, 'wert' | 'laedt' | 'ausgefallen' | 'erneut'>

/**
 * Die Kontenzuordnung: welches lokale Konto auf welches Konzernkonto läuft
 * (B8★, R138, R303, R306).
 *
 * **Sie ist der Grund, warum zwei Kontenrahmen nebeneinander bestehen dürfen.**
 * Die Produktion bucht auf `0090 Grundstücke und Bauten`, die Inc. auf
 * `1500 Land and buildings` — beide werden KTO-0210, und keine der beiden muss
 * ihren Kontenrahmen aufgeben.
 *
 * **Zeitbezogen und fortgeltend** (R1–R3): Eine Zuordnung gilt ab einer
 * Periode und bleibt, bis eine neue Fassung sie ablöst. Was im Januar
 * zugeordnet war, ist im Februar zugeordnet — deshalb gibt es für bereits
 * zugeordnete Konten auch keinen Vorschlag mehr (R138).
 *
 * **Ein Nachschlagewerk aller getätigten Zuordnungen** (5.1 Abschnitt 3.68,
 * R1852, seit dem 2026-10-16): Suche und Gesellschaft über den Tabellen, keine
 * Kopfzeile. Zugeordnet wird im Datenimport (R589); den Teil *Vorbereiten,
 * ohne zu importieren* mit Ablagefläche und Gliederung gibt es nicht mehr
 * (R1854). Berichtigen, Stilllegen und Verlauf sitzen im Zeilenmenü.
 */

/**
 * Die Namen der Zielfelder, die dem Haus gehören und keinen eigenen tragen.
 *
 * **Die Dimensionen stehen nicht darin:** Sie heißen, wie der Kunde sie nennt (R522).
 */
const FELDNAMEN: Record<string, string> = {
  ic_partner: 'Partner',
  segment: 'Segment',
  funktionsbereich: 'Funktionsbereich',
}

/**
 * Wie ein Element des **eigenen** Abschlusses am Tisch heißt (R703, R705).
 *
 * **Seit dem 2026-09-22 entscheidet das der Dienst.** Hier stand ein Vergleich
 * von `code` gegen `schluessel` — er traf den Fall richtig, an dem er gebaut
 * war, und hätte `EUR — Euro` mit abgeräumt: Bei Währung und Land **ist** der
 * Schlüssel der Code der Weltnorm. Der Dienst schickt das Codefeld jetzt leer,
 * wo die Objektart keines führt; hier bleibt der Rückfall auf den Schlüssel,
 * damit keine Zelle leer ist (R238).
 */
function lesbar(e: { schluessel: string; code: string; namen: Beschriftung }): string {
  return mitCode(e.code, beschriftung(e.namen), ' ', e.schluessel)
}

export function Kontenzuordnung({
  periode = laufenderMonat(),
}: {
  /** Der Monat der Spalte *Position* und der Vorschlag beim Stilllegen — ohne Angabe der laufende (R1853). */
  periode?: number
} = {}) {
  /*
    **Die Spaltenbreiten zieht der Anwender** (7.3 Abschnitt 10, R415 —
    Auftrag vom 2026-09-29). Hier je Spalte eine Breite: Die Spalten dieser
    Tabelle tragen verschiedene Dinge und haben nichts gleichzumachen.
  */
  const { masse: spaltenmasse1, rahmen: spaltenrahmen1 } =
    useSpaltenmasse<HTMLTableElement>('kontenzuordnung:kontenzuordnung:1')

  /*
    **Die Spaltenbreiten zieht der Anwender** (7.3 Abschnitt 10, R415 —
    Auftrag vom 2026-09-29). Hier je Spalte eine Breite: Die Spalten dieser
    Tabelle tragen verschiedene Dinge und haben nichts gleichzumachen.
  */
  const { masse: spaltenmasse2, rahmen: spaltenrahmen2 } =
    useSpaltenmasse<HTMLTableElement>('kontenzuordnung:kontenzuordnung:2')

  /*
    **Die Spaltenbreiten zieht der Anwender** (7.3 Abschnitt 10, R415 —
    Auftrag vom 2026-09-29). Hier je Spalte eine Breite: Die Spalten dieser
    Tabelle tragen verschiedene Dinge und haben nichts gleichzumachen.
  */
  const { masse: spaltenmasse3, rahmen: spaltenrahmen3 } =
    useSpaltenmasse<HTMLTableElement>('kontenzuordnung:kontenzuordnung:3')

  const [einheiten, setEinheiten] = useState<Katalogeintrag[]>([])
  const [konten, setKonten] = useState<Katalogeintrag[]>([])
  const [mapping, setMapping] = useState<Kontenmapping[]>([])
  const [bewegungsarten, setBewegungsarten] = useState<Katalogeintrag[]>([])
  /** Das Suchfeld der drei Tabellen (R601). */
  const [tabellensuche, setTabellensuche] = useState('')
  /**
   * Die gewählte Gesellschaft — leer heißt alle (R1153). Gezeigt wird, was
   * ihre geltenden Lieferungen verwenden, nicht, was sie ausgelöst hat.
   */
  const [gesellschaft, setGesellschaft] = useGemerkt('kontenzuordnung.gesellschaft', '')
  /** Das Zeilenmenü, wo es gerade offen steht (R1162). */
  const [menue, setMenue] = useState<Menueort | null>(null)
  /** Die Zeile, vor deren Stilllegen gefragt wird (R1161). */
  const [rueckfrage, setRueckfrage] = useState<Zeilenbezug | null>(null)
  const [rueckfragefehler, setRueckfragefehler] = useState<string | null>(null)
  /** Die Zeile, die gerade berichtigt wird (R1170–R1172). */
  const [berichtigung, setBerichtigung] = useState<Zeilenbezug | null>(null)
  /** Die Zeile, deren Verlauf offen steht (R1175). */
  const [verlauf, setVerlauf] = useState<Zeilenbezug | null>(null)

  /*
    **Was der Anwender nicht darf, ist nicht da** (8.1 Abschnitt 7, R1180):
    Ein Prüfer sieht die drei Tabellen, die Excel-Mappe und den Verlauf — nicht
    Berichtigen, Stilllegen und Wieder in Kraft setzen.
  */
  const darf = useDarf()

  /** Was eine Zeile darf — je Sorte ein anderer Handgriff (R1180). */
  function darfAnZeile(sorte: Zeilenbezug['sorte'], tat: 'berichtigen' | 'stilllegen' | 'inkraft'): boolean {
    if (sorte === 'konto') {
      return tat === 'berichtigen'
        ? darf('zuordnen')
        : tat === 'stilllegen'
          ? darf('zuordnungStilllegen')
          : darf('zuordnungInKraftSetzen')
    }
    if (sorte === 'bewegungsart') {
      return tat === 'berichtigen'
        ? darf('bewegungsartZuordnen')
        : tat === 'stilllegen'
          ? darf('bewegungsartzuordnungStilllegen')
          : darf('bewegungsartzuordnungInKraftSetzen')
    }
    return tat === 'berichtigen'
      ? darf('wertZuordnen')
      : tat === 'stilllegen'
        ? darf('wertzuordnungStilllegen')
        : darf('wertzuordnungInKraftSetzen')
  }

  const [stand, setStand] = useState(0)
  const [meldung, setMeldung] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)

  // Der Katalog wird bei jeder Änderung neu gelesen: Ein eben erzeugtes Konto
  // stünde sonst in der Tabelle darunter mit seinem Schlüssel statt mit Nummer
  // und Namen — und sähe aus wie ein Fehler.
  // Der Katalog liefert Namen und Wahlvorrat — ein Nachschlagen im Hintergrund (R1007).
  useEffect(() => {
    let gilt = true
    imHintergrund(() => api.stammdatenkatalog())
      .then((k) => {
        if (!gilt) {
          return
        }
        setEinheiten(k.einheiten)
        setKonten(k.konten)
        setBewegungsarten(k.bewegungsarten)
        // Gemerkt und noch da, sonst „alle" (R1363, R1364).
        const da = (s: string) => k.einheiten.some((e) => e.schluessel === s)
        setGesellschaft((jetzt) => (jetzt && !da(jetzt) ? '' : jetzt))
      })
      .catch(() => undefined)

    return () => {
      gilt = false
    }
  }, [stand])

  // Die Zielfelder liefern Namen und Wahlvorrat — im Hintergrund (R1007, R1211).
  const zielfelder = useHintergrundabruf(() => api.zielfelder(), [stand], KEINE_ZIELFELDER)

  // Die Vorräte der beiden Auswahlfelder (7.2 Abschnitt 10, R583).
  const einheitenwahl = useMemo(() => einheiten.map(ausStammdatum), [einheiten])
  /** Die Ziele, unter denen beim Berichtigen gewählt wird (R1172). */
  // Einem technischen Konto wird kein lokales zugeordnet (R1427).
  // Ohne Statistikkonten: Eine Lieferung darf keines tragen (R1732, R1734).
  const kontenwahl = useMemo(
    () => ohneStatistikkonten(ohneTechnische(konten, (k) => k.schluessel), (k) => k.schluessel).map(ausStammdatum),
    [konten],
  )
  const bewegungsartwahl = useMemo(() => bewegungsarten.map(ausStammdatum), [bewegungsarten])

  /** Die Werte eines Zielfelds — beim Partner auch die Konzerngesellschaften (R600). */
  function wertwahl(feld: string) {
    const aus = (zielfelder.find((z) => z.name === feld)?.werte ?? []).map((v) => ({
      wert: v.schluessel,
      text: lesbar(v),
      suchtext: [v.schluessel, v.code, ...Object.values(v.namen)].join(' '),
      stillgelegtAb: v.stillgelegtAb,
    }))
    return feld === 'ic_partner' ? [...einheitenwahl, ...aus] : aus
  }

  /** Der feste Partner einer Kontenzuordnung (R750) — oder ausdrücklich keiner. */
  const partnerwahl = useMemo(
    () => [{ wert: '', text: t('mapping.berichtigen.ohnepartner') }, ...wertwahl('ic_partner')],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [einheitenwahl, zielfelder],
  )

  const gesellschaftswahl = useMemo(
    () => [{ wert: '', text: t('mapping.gesellschaft.alle') }, ...einheitenwahl],
    [einheitenwahl],
  )

  // **Universell gelesen** (R309): Die Zuordnung gilt für alle Gesellschaften,
  // also zeigt die Tabelle auch alle. Die Gesellschaft bleibt als Herkunft an
  // der Zeile stehen — sie sagt, wer sie veranlasst hat, und filtert nicht.
  useEffect(() => {
    api.kontenmapping().then(setMapping)
  }, [stand])

  // Dieselbe Sicht für die beiden neuen Tische: universell gelesen, die
  // Gesellschaft als Herkunft an der Zeile (R309, R593, R597).
  // **Haupt-, nicht Hintergrundabrufe** (R1211): Die Zuordnungen vom
  // 2026-09-18 (R593, R597) sind der Bestand der beiden Tabellen. Bis zum
  // 2026-10-06 liefen sie still, und ein Aussetzer ergab „Es ist noch kein
  // Bewegungsartcode zugeordnet."
  const artabruf = useAbruf(() => api.bewegungsartmapping(), [stand])
  const artmapping = artabruf.wert ?? KEINE_ARTZUORDNUNGEN
  const wertabruf = useAbruf(() => api.wertzuordnungen(), [stand])
  const wertmapping = wertabruf.wert ?? KEINE_WERTZUORDNUNGEN

  // Welche Zuordnungen die gewählte Gesellschaft verwendet (R1153) — nach
  // jedem Stilllegen neu, damit die Zählung stimmt. Ein Hauptabruf (R1211):
  // Er entscheidet, welche Zeilen dastehen; kam er nicht, wäre jede Tabelle
  // leer und spräche von der Gesellschaft.
  const verwendetabruf = useAbruf(
    gesellschaft ? () => api.verwendeteZuordnungen(gesellschaft) : null,
    [gesellschaft, stand],
  )
  const verwendet = verwendetabruf.wert

  /*
    **Die Position in jeder Gliederung** (5.1 Abschnitt 3.68, R1853): ohne
    Einschränkung nach Bewertungsebene, zum Monat — ohne Angabe der laufende.
    Die Seite ist ein Nachschlagewerk (R1852). Ein Konto an zwei Positionen
    nennt beide; tragen mehr als eine Gliederung Konten, steht ihr Name davor.
    Die Excel-Mappe liest dieselbe Abfrage.
  */
  // **Ein Hauptabruf** (7.2 Abschnitt 39, R1211): Kam die Antwort nicht, steht
  // über der Tabelle „Ließ sich nicht laden" und die Spalte bleibt leer —
  // nicht „—" in jeder Zeile, als hätte kein Konto eine Position (R1210).
  const positionsabruf = useAbruf(() => api.kontopositionen(periode), [periode, stand])
  const positionen = useMemo(() => {
    const stellen = positionsabruf.wert
    if (!stellen) {
      return null
    }

    const mitGliederung = new Set(stellen.map((s) => s.plan)).size > 1
    const je = new Map<string, string[]>()
    for (const s of stellen) {
      const stelle = mitCode(s.code, beschriftung(s.namen), ' ')
      const text = mitGliederung ? `${beschriftung(s.plannamen) || s.plan}: ${stelle}` : stelle
      je.set(s.konto, [...(je.get(s.konto) ?? []), text])
    }
    return new Map([...je].map(([konto, texte]) => [konto, [...new Set(texte)].join(' · ')]))
  }, [positionsabruf.wert])

  /**
   * Legt still — ab dem Monat aus der Rückfrage, jede Sorte auf ihrem Weg
   * (R305, R1160). Ohne „die Fassung bleibt lesbar" (7.2 Abschnitt 34, R1131).
   * Geht es schief, steht der Grund in der Rückfrage, die offen bleibt.
   */
  async function legeStill(bezug: Zeilenbezug, ab: number) {
    setLaeuft(true)
    setRueckfragefehler(null)
    try {
      if (bezug.sorte === 'konto') {
        await api.zuordnungStilllegen(bezug.schluessel, ab)
      } else if (bezug.sorte === 'bewegungsart') {
        await api.bewegungsartzuordnungStilllegen(bezug.schluessel, ab)
      } else {
        await api.wertzuordnungStilllegen(bezug.schluessel, ab)
      }
      meldeErfolg(t('erfolg.kontenzuordnung.stillgelegt'))
      setMeldung(null)
      setRueckfrage(null)
      setStand((n) => n + 1)
    } catch (f) {
      setRueckfragefehler(fachlicherSatz(f))
    } finally {
      setLaeuft(false)
    }
  }

  /** Nimmt das Ende zurück, als neue Fassung (R1163). */
  async function setzeInKraft(bezug: Zeilenbezug) {
    setLaeuft(true)
    try {
      if (bezug.sorte === 'konto') {
        await api.zuordnungInKraftSetzen(bezug.schluessel)
      } else if (bezug.sorte === 'bewegungsart') {
        await api.bewegungsartzuordnungInKraftSetzen(bezug.schluessel)
      } else {
        await api.wertzuordnungInKraftSetzen(bezug.schluessel)
      }
      meldeErfolg(t('erfolg.kontenzuordnung.inkraft'))
      setMeldung(null)
      setStand((n) => n + 1)
    } catch (f) {
      setMeldung(fachlicherSatz(f))
    } finally {
      setLaeuft(false)
    }
  }

  /**
   * Ob eine Zuordnung zur gewählten Gesellschaft gehört (R1153) — ohne Wahl
   * jede. Solange die Antwort fehlt, keine: Eine Liste, die erst alles zeigt
   * und dann schrumpft, sähe einen Augenblick lang wie das Ergebnis aus.
   */
  function gewaehlt(liste: (v: VerwendeteZuordnungen) => string[], schluessel: string): boolean {
    if (!gesellschaft) {
      return true
    }
    return verwendet !== null && verwendet.einheit === gesellschaft && liste(verwendet).includes(schluessel)
  }

  const gesucht = tabellensuche.trim().length > 0

  /** Was nichts zeigt, sagt warum: nichts gefunden, nichts verwendet, nichts da (R145). */
  function leer(ohneAlles: Parameters<typeof t>[0]): Parameters<typeof t>[0] {
    return gesucht ? 'mapping.keintreffer' : gesellschaft ? 'mapping.gesellschaft.leer' : ohneAlles
  }

  /**
   * Was in einer leeren Tabelle steht (R1210): Kam ihr Bestand oder die Wahl
   * der Gesellschaft nicht, „Ließ sich nicht laden" statt des Satzes über
   * einen leeren Bestand; kommt der Bestand erst noch, nichts.
   */
  function leerstelle(abrufe: Teilabruf[], ohneAlles: Parameters<typeof t>[0]) {
    const beteiligt = gesellschaft ? [...abrufe, verwendetabruf] : abrufe
    const ausgefallen = beteiligt.filter((a) => a.ausgefallen)
    if (ausgefallen.length > 0) {
      return <Abrufausfall abruf={{ erneut: () => ausgefallen.forEach((a) => a.erneut()) }} />
    }
    if (abrufe.some((a) => a.laedt && a.wert === null)) {
      return null
    }
    return t(leer(ohneAlles))
  }

  const zugeordnet = useMemo(() => {
    const wort = tabellensuche.trim().toLowerCase()
    return [...mapping]
      .filter((m) => gewaehlt((v) => v.konten, m.schluessel))
      .filter((m) =>
        wort.length === 0 ||
        [m.lokalnummer, m.lokalbezeichnung ?? '', kontoname(m.konto)]
          .some((s) => s.toLowerCase().includes(wort)),
      )
      .sort((a, b) => a.lokalnummer.localeCompare(b.lokalnummer))
  }, [mapping, tabellensuche, konten, gesellschaft, verwendet])

  /*
    **Dasselbe Suchfeld für alle drei Tabellen** (R601): Wer nach „Segment“
    oder nach „Z01“ sucht, sucht nach einer Zuordnung und nicht nach einer
    Tabelle. Gesucht wird über alles, was in der Zeile steht.
  */
  const gefundeneArten = useMemo(() => {
    const wort = tabellensuche.trim().toLowerCase()
    return [...artmapping]
      .filter((m) => gewaehlt((v) => v.bewegungsarten, m.schluessel))
      .filter((m) =>
        wort.length === 0 ||
        [m.code, m.bezeichnung ?? '', bewegungsartname(m.bewegungsart)]
          .some((s) => s.toLowerCase().includes(wort)),
      )
      .sort((a, b) => a.code.localeCompare(b.code))
  }, [artmapping, tabellensuche, bewegungsarten, gesellschaft, verwendet])

  const gefundeneWerte = useMemo(() => {
    const wort = tabellensuche.trim().toLowerCase()
    return [...wertmapping]
      .filter((w) => gewaehlt((v) => v.werte, w.schluessel))
      .filter((w) =>
        wort.length === 0 ||
        [feldname(w.feld), w.code, w.bezeichnung ?? '', zielname(w.feld, w.ziel)]
          .some((s) => s.toLowerCase().includes(wort)),
      )
      .sort((a, b) => a.feld.localeCompare(b.feld) || a.code.localeCompare(b.code))
  }, [wertmapping, tabellensuche, zielfelder, gesellschaft, verwendet])

  /**
   * Das Gelieferte in einer Zelle, wie das Ziel daneben (R1152, R838):
   * „1010 Cash - Operating Account", nicht Nummer und Bezeichnung in zwei
   * Spalten.
   */
  function geliefert(code: string, bezeichnung: string | null): string {
    return mitCode(code, bezeichnung ?? '', ' ')
  }

  /** Der Name eines Zielfelds — die Dimensionen heißen, wie der Kunde sie nennt (R522). */
  function feldname(feld: string): string {
    const ziel = zielfelder.find((z) => z.name === feld)
    const eigen = ziel?.namen ? beschriftung(ziel.namen) : ''
    return eigen || FELDNAMEN[feld] || feld
  }

  /**
   * Der Zielwert, wie ein Mensch ihn liest (R552, R703).
   *
   * **Nicht `SEG-DL`:** Die Kennung ist für die Maschine. Beim Partner steht
   * das Ziel in zwei Vorräten — Konzerngesellschaften und Partnercodes (R600).
   *
   * **Bis zum 2026-09-22 stand die Kennung trotzdem da.** Die drei Helfer hier
   * setzten `code` vor den Namen, und `code` ist seit R676 der Schlüssel —
   * beim Konto stand die Nummer dadurch zweimal. Sie steht ohnehin vorn in der
   * Bezeichnung (R678).
   */
  function zielname(feld: string, schluessel: string): string {
    const ausVorrat = zielfelder
      .find((z) => z.name === feld)
      ?.werte.find((v) => v.schluessel === schluessel)

    return ausVorrat ? lesbar(ausVorrat) : gesellschaftsname(schluessel)
  }

  // Gesellschaft und Bewegungsart heißen, wie der Namensgeber sie nennt (R1660):
  // bis zum 2026-10-12 aus eigenen Listen, mit dem Code als Rückfall.

  // Auch das Konto heißt, wie der Namensgeber es nennt (R1660).
  function kontoname(schluessel: string): string {
    return kontobezeichnung(schluessel)
  }

  return (
    /*
      **Die drei Tabellen sind keine Bilanz** (R704). `.tabellenrahmen table`
      trägt 1120 Pixel Mindestbreite und eine 330 Pixel breite erste Spalte —
      beides für einen Bericht mit vielen Wertspalten und einer stehenden
      Positionsbezeichnung. Hier steht links eine vierstellige Kontonummer, und
      das Ergebnis war ein waagerechter Schieber unter jeder der drei Tabellen,
      **auch wenn keine Zeile darin stand**. Die Klasse nimmt beides zurück;
      derselbe Weg wie beim Belegordner.
    */
    <section className="zuordnungsblatt">
      <h1>{t('bereich.datenimport.importmapping')}</h1>

      {/*
        **Der Bereich ist nachrichtlich** (R592, seit dem 2026-09-18).
        Zugeordnet wird im Lauf, im Fenster über der Importvorschau (R589) —
        dort steht die Datei schon gelesen, und dort fällt auf, dass ein Konto
        fehlt. Hier steht deshalb zuerst, was gilt: Einsicht, Stilllegen und
        der Nachtrag von Hand.
      */}
      {/* **Ein Suchfeld für alle drei Tabellen** (R601): Wer sucht, sucht nach
          einer Zuordnung und nicht nach einer Tabelle. Daneben die Gesellschaft
          (R1153) und die Mappe (R1155) — Angaben und Ausgabe des Laufs stehen
          in einer Zeile (R609, R1164). */}
      <div className="feldzeile">
        <label className="suchzeile">
          <span className="name">{t('mapping.suche')}</span>
          <input
            value={tabellensuche}
            onChange={(e) => setTabellensuche(e.target.value)}
            placeholder={t('mapping.suche.beispiel')}
          />
        </label>

        <label>
          <span className="name">{t('mapping.gesellschaft')}</span>
          <Auswahlfeld
            wert={gesellschaft}
            eintraege={gesellschaftswahl}
            beschriftung={t('mapping.gesellschaft')}
            aufWahl={setGesellschaft}
          />
        </label>

        {/*
          **Die Mappe gibt aus, was die Auswahl zeigt** (R1155). Ein Verweis und
          kein Knopf — eine Datei, die der Browser herunterlädt, soll er auch
          selbst holen.
        */}
        <a
          className="haupt"
          href={mappenadresse(gesellschaft, periode)}
          download
        >
          {t('mapping.export')}
        </a>
      </div>

      {/*
        **Jede Tabelle klappt auf und zu** (R1151) — zuerst zu, danach so, wie
        der Anwender sie zuletzt hatte. Eine Suche mit Treffer klappt auf.

        **Die Zeile trägt keinen Knopf** (R1162, Befund vom 2026-10-06: 205
        Knöpfe auf fünf Bildschirmhöhen). Stilllegen und Wieder in Kraft setzen
        sitzen im Zeilenmenü — Rechtsklick, Kontextmenü-Taste oder der Knopf
        am Zeilenanfang, der beim Überfahren erscheint.
      */}
      <Blattabschnitt
        kennung="zuordnungen.konten"
        titel={t('mapping.tabelle.titel', { anzahl: anzahlwort(zugeordnet.length) })}
        vorgabe={false}
        auf={gesucht && zugeordnet.length > 0}
      >
        {positionsabruf.ausgefallen && <Abrufausfall abruf={positionsabruf} satz={t('mapping.position.ausgefallen')} />}
        <div className="tabellenrahmen">
          <table ref={spaltenrahmen1} className="ziehbaretabelle">
            <Spaltenmassleiste masse={spaltenmasse1} />
            <thead>
              <tr>
                {/* **Eine Spalte für das Gelieferte wie für das Ziel** (R1152). */}
                <th>{t('mapping.spalte.lokalnummer')}</th>
                <th>{t('mapping.spalte.konzernkonto')}</th>
                <th>{t('mapping.spalte.position')}</th>
                <th>{t('mapping.spalte.gueltig')}</th>
              </tr>
            </thead>
            <tbody>
              {zugeordnet.map((m) => (
                <Zuordnungszeile
                  key={m.schluessel}
                  bezug={{
                    sorte: 'konto',
                    schluessel: m.schluessel,
                    einheit: m.einheit,
                    code: m.lokalnummer,
                    bezeichnung: m.lokalbezeichnung,
                    zielSchluessel: m.konto,
                    partner: m.icPartner ?? null,
                    quelle: geliefert(m.lokalnummer, m.lokalbezeichnung),
                    ziel: kontoname(m.konto),
                    gueltigVon: m.gueltigVon,
                    gueltigBis: m.gueltigBis,
                  }}
                  nachspalte={
                    // Leer nur, solange die Spalte nicht kam oder ausfiel — ein
                    // Konto ohne Stelle in einer Gliederung trägt den Strich (R1853, R1210).
                    positionsabruf.ausgefallen || (positionsabruf.laedt && positionen === null)
                      ? ''
                      : (positionen?.get(m.konto) ?? '—')
                  }
                  aufMenue={setMenue}
                />
              ))}
              {zugeordnet.length === 0 && (
                <tr>
                  <td colSpan={4} className="hinweis">
                    {/* **Nichts da ist etwas anderes als nichts gefunden** (R145). */}
                    {leerstelle([], 'mapping.leer')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Blattabschnitt>

      {/*
        **Alle Zuordnungen an einem Ort** (R601, seit dem 2026-09-18) — für
        gelieferte Bewegungsartcodes (R593) und für jeden anderen gelieferten
        Wert (R597). **Angelegt wird weiter im Lauf** (R589, R595): Dort fällt
        auf, dass etwas fehlt, und dort steht die Datei schon gelesen. Hier
        steht, was gilt.
      */}
      <Blattabschnitt
        kennung="zuordnungen.bewegungsarten"
        titel={t('mapping.bewegungsart.titel', { anzahl: anzahlwort(gefundeneArten.length) })}
        vorgabe={false}
        auf={gesucht && gefundeneArten.length > 0}
      >
        <div className="tabellenrahmen">
          <table ref={spaltenrahmen2} className="ziehbaretabelle">
            <Spaltenmassleiste masse={spaltenmasse2} />
            <thead>
              <tr>
                <th>{t('mapping.spalte.code')}</th>
                <th>{t('mapping.spalte.bewegungsart')}</th>
                <th>{t('mapping.spalte.gueltig')}</th>
              </tr>
            </thead>
            <tbody>
              {gefundeneArten.map((m) => (
                <Zuordnungszeile
                  key={m.schluessel}
                  bezug={{
                    sorte: 'bewegungsart',
                    schluessel: m.schluessel,
                    einheit: m.einheit,
                    code: m.code,
                    bezeichnung: m.bezeichnung,
                    zielSchluessel: m.bewegungsart,
                    partner: null,
                    quelle: geliefert(m.code, m.bezeichnung),
                    ziel: bewegungsartname(m.bewegungsart),
                    gueltigVon: m.gueltigVon,
                    gueltigBis: m.gueltigBis,
                  }}
                  aufMenue={setMenue}
                />
              ))}
              {gefundeneArten.length === 0 && (
                <tr>
                  <td colSpan={3} className="hinweis">
                    {leerstelle([artabruf], 'mapping.bewegungsart.leer')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Blattabschnitt>

      <Blattabschnitt
        kennung="zuordnungen.werte"
        titel={t('mapping.werte.titel', { anzahl: anzahlwort(gefundeneWerte.length) })}
        vorgabe={false}
        auf={gesucht && gefundeneWerte.length > 0}
      >
        <div className="tabellenrahmen">
          <table ref={spaltenrahmen3} className="ziehbaretabelle">
            <Spaltenmassleiste masse={spaltenmasse3} />
            <thead>
              <tr>
                {/*
                  **Das Feld steht in der Zeile, nicht in der Überschrift**
                  (R601): Eine Tabelle je Feld wären elf, von denen zehn meistens
                  leer sind.
                */}
                <th>{t('mapping.spalte.feld')}</th>
                <th>{t('mapping.spalte.code')}</th>
                <th>{t('mapping.spalte.ziel')}</th>
                <th>{t('mapping.spalte.gueltig')}</th>
              </tr>
            </thead>
            <tbody>
              {gefundeneWerte.map((w) => (
                <Zuordnungszeile
                  key={w.schluessel}
                  vorspalte={feldname(w.feld)}
                  bezug={{
                    sorte: 'wert',
                    schluessel: w.schluessel,
                    einheit: w.einheit,
                    code: w.code,
                    bezeichnung: w.bezeichnung,
                    feld: w.feld,
                    zielSchluessel: w.ziel,
                    partner: null,
                    quelle: geliefert(w.code, w.bezeichnung),
                    ziel: zielname(w.feld, w.ziel),
                    gueltigVon: w.gueltigVon,
                    gueltigBis: w.gueltigBis,
                  }}
                  aufMenue={setMenue}
                />
              ))}
              {gefundeneWerte.length === 0 && (
                <tr>
                  <td colSpan={4} className="hinweis">
                    {leerstelle([wertabruf], 'mapping.werte.leer')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Blattabschnitt>

      {menue && (
        <Zeilenmenue
          x={menue.x}
          y={menue.y}
          punkte={[
            {
              schluessel: 'mapping.menue.berichtigen',
              wenn: darfAnZeile(menue.bezug.sorte, 'berichtigen'),
              tun: () => setBerichtigung(menue.bezug),
            },
            {
              schluessel: 'mapping.menue.verlauf',
              tun: () => setVerlauf(menue.bezug),
            },
            {
              schluessel: 'mapping.menue.stilllegen',
              wenn: menue.bezug.gueltigBis === null && darfAnZeile(menue.bezug.sorte, 'stilllegen'),
              tun: () => setRueckfrage(menue.bezug),
            },
            {
              schluessel: 'mapping.menue.inkraft',
              wenn: menue.bezug.gueltigBis !== null && darfAnZeile(menue.bezug.sorte, 'inkraft'),
              tun: () => void setzeInKraft(menue.bezug),
            },
          ]}
          aufSchliessen={() => setMenue(null)}
        />
      )}

      {berichtigung && (
        <Berichtigungsfenster
          bezug={berichtigung}
          zielwahl={
            berichtigung.sorte === 'konto'
              ? kontenwahl
              : berichtigung.sorte === 'bewegungsart'
                ? bewegungsartwahl
                : wertwahl(berichtigung.feld ?? '')
          }
          partnerwahl={berichtigung.sorte === 'konto' ? partnerwahl : []}
          aufBerichtigt={() => {
            setBerichtigung(null)
            setStand((n) => n + 1)
          }}
          aufSchliessen={() => setBerichtigung(null)}
        />
      )}

      {verlauf && (
        <Verlaufsfenster
          bezug={verlauf}
          zielname={(s) =>
            verlauf.sorte === 'konto'
              ? kontoname(s)
              : verlauf.sorte === 'bewegungsart'
                ? bewegungsartname(s)
                : zielname(verlauf.feld ?? '', s)
          }
          partnername={(s) => partnerwahl.find((p) => p.wert === s)?.text ?? s}
          aufSchliessen={() => setVerlauf(null)}
        />
      )}

      {rueckfrage && (
        <Stilllegefrage
          bezug={rueckfrage}
          periode={periode}
          gesellschaftsname={gesellschaftsname}
          laeuft={laeuft}
          fehler={rueckfragefehler}
          aufStilllegen={(ab) => void legeStill(rueckfrage, ab)}
          aufSchliessen={() => {
            setRueckfrage(null)
            setRueckfragefehler(null)
          }}
        />
      )}

      {meldung && <p className="hinweis">{meldung}</p>}

    </section>
  )
}

/** Was Zeilenmenü und Rückfrage von einer Zeile wissen müssen (R1161, R1162). */
type Zeilenbezug = {
  sorte: 'konto' | 'bewegungsart' | 'wert'
  schluessel: string
  /** Wer die Zuordnung veranlasst hat — für das Berichtigen, das sie fortschreibt. */
  einheit: string
  /** Das Gelieferte in seinen Teilen: Nummer bzw. Code, Bezeichnung, bei Werten das Feld. */
  code: string
  bezeichnung: string | null
  feld?: string
  /** Der Schlüssel des heutigen Ziels und der feste Partner (R1172). */
  zielSchluessel: string
  partner: string | null
  /** Das Gelieferte, wie es in der Zelle steht — „1200 Accounts Receivable". */
  quelle: string
  ziel: string
  gueltigVon: number
  gueltigBis: number | null
}

type Menueort = { x: number; y: number; bezug: Zeilenbezug }

/** Der Zeitraum in einer Zelle (R1164): „ab Januar 2025" oder „Januar 2025 bis September 2026". */
function zeitraum(von: number, bis: number | null): string {
  return bis === null
    ? t('mapping.gueltig.ab', { von: periodenname(von) })
    : t('mapping.gueltig.bis', { von: periodenname(von), bis: periodenname(bis) })
}

/**
 * Die Adresse der Excel-Mappe — mit der Gesellschaft der Auswahl (R1155) und
 * dem Monat der Spalte *Position* (R1853).
 */
function mappenadresse(gesellschaft: string, periode: number): string {
  const frage = new URLSearchParams()
  if (gesellschaft) {
    frage.set('einheit', gesellschaft)
  }
  frage.set('periode', String(periode))
  const text = frage.toString()
  return '/api/datenimport/zuordnungen/export' + (text ? '?' + text : '')
}

/** Der Monat davor — über den Jahreswechsel der Dezember. */
function vormonat(periode: number): number {
  return periode % 100 === 1 ? periode - 100 + 11 : periode - 1
}

/**
 * Eine Zeile der drei Tabellen — **ohne Knopf** (R1162).
 *
 * Das Menü geht auf drei Wegen auf, wie an jeder Berichtszeile: Rechtsklick,
 * Kontextmenü-Taste auf der fokussierten Zeile — dafür trägt sie `tabIndex` —
 * und der Knopf am Zeilenanfang, der beim Überfahren erscheint.
 */
function Zuordnungszeile({
  bezug,
  vorspalte,
  nachspalte,
  aufMenue,
}: {
  bezug: Zeilenbezug
  /** Was vor dem Gelieferten steht — bei den weiteren Werten das Feld. */
  vorspalte?: string
  /** Was nach dem Ziel steht — bei den Konten die Position (R1186). */
  nachspalte?: string
  aufMenue: (ort: Menueort) => void
}) {
  return (
    <tr
      tabIndex={0}
      className={bezug.gueltigBis !== null ? 'stillgelegt' : undefined}
      onContextMenu={(e) => {
        e.preventDefault()
        aufMenue({ ...menueort(e, e.currentTarget), bezug })
      }}
    >
      <td>
        <Menueknopf aufMenue={(x, y) => aufMenue({ x, y, bezug })} />
        {vorspalte ?? bezug.quelle}
      </td>
      {vorspalte !== undefined && <td>{bezug.quelle}</td>}
      <td>{bezug.ziel}</td>
      {nachspalte !== undefined && <td>{nachspalte}</td>}
      <td className="zeitraum">{zeitraum(bezug.gueltigVon, bezug.gueltigBis)}</td>
    </tr>
  )
}

/**
 * Die Rückfrage vor dem Stilllegen (5.1 Abschnitt 3.50, R1160, R1161).
 *
 * **Sie nennt, wen es trifft** — jede Gesellschaft, deren geltende
 * Lieferungen die Zuordnung verwenden. Der Befund vom 2026-10-06: Wer oben
 * *Tochter USA* gewählt hatte und stilllegte, nahm die Zuordnung auch Holding
 * und Tochter 1, ohne dass es irgendwo stand.
 *
 * **Und ab wann:** Vorgabe ist der laufende Monat; frühere behalten sie.
 */
function Stilllegefrage({
  bezug,
  periode,
  gesellschaftsname,
  laeuft,
  fehler,
  aufStilllegen,
  aufSchliessen,
}: {
  bezug: Zeilenbezug
  periode: number
  gesellschaftsname: (schluessel: string) => string
  laeuft: boolean
  fehler: string | null
  aufStilllegen: (ab: number) => void
  aufSchliessen: () => void
}) {
  const [ab, setAb] = useState<number | null>(periode)
  const [verwender, setVerwender] = useState<string[] | 'unbekannt' | null>(null)

  useEffect(() => {
    let gilt = true
    api
      .zuordnungVerwender(bezug.sorte, bezug.schluessel)
      .then((v) => gilt && setVerwender(v))
      .catch(() => gilt && setVerwender('unbekannt'))

    return () => {
      gilt = false
    }
  }, [bezug])

  return (
    <Dialogfenster
      titel={t('mapping.stilllegen.titel', { quelle: bezug.quelle, ziel: bezug.ziel })}
      weite="schmal"
      aufSchliessen={aufSchliessen}
      fuss={
        <>
          <button
            type="button"
            className="gefahr"
            disabled={laeuft || ab === null}
            onClick={() => ab !== null && aufStilllegen(ab)}
          >
            {t('loeschen.tun.stilllegen', { periode: ab === null ? '' : periodenname(ab) })}
          </button>
          <button type="button" onClick={aufSchliessen}>
            {t('loeschen.zurueck')}
          </button>
        </>
      }
    >
      <p className="verwender">
        {verwender === null
          ? t('mapping.stilllegen.laedt')
          : verwender === 'unbekannt'
            ? t('mapping.stilllegen.unbekannt')
            : verwender.length === 0
              ? t('mapping.stilllegen.niemand')
              : t('mapping.stilllegen.verwender', { liste: verwender.map(gesellschaftsname).join(', ') })}
      </p>

      <Periodenfeld
        wert={ab}
        aufWert={setAb}
        beschriftung={t('mapping.stilllegen.ab')}
        klasse=""
        erlaeuterung={
          ab === null
            ? undefined
            : ab <= bezug.gueltigVon
              ? t('mapping.stilllegen.ganz')
              : t('mapping.stilllegen.bis', { monat: periodenname(vormonat(ab)) })
        }
      />

      {fehler && <p className="hinweis">{fehler}</p>}
    </Dialogfenster>
  )
}

/** Ein Eintrag einer Auswahl — wie ihn das Auswahlfeld erwartet. */
type Wahl = { wert: string; text: string; suchtext?: string }

/**
 * Eine Zuordnung berichtigen — Ziel und, bei Konten, Partner, **ab einem
 * Monat, den der Anwender einträgt** (5.1 Abschnitt 3.51, R1170–R1172).
 *
 * **Das Monatsfeld ist leer** (R1171): Wer berichtigt, entscheidet über die
 * Vergangenheit — frühere Monate behalten das bisherige Ziel —, und das soll
 * er nicht durch Bestätigen einer Vorgabe tun.
 */
function Berichtigungsfenster({
  bezug,
  zielwahl,
  partnerwahl,
  aufBerichtigt,
  aufSchliessen,
}: {
  bezug: Zeilenbezug
  zielwahl: Wahl[]
  /** Nur bei Konten — sonst leer. */
  partnerwahl: Wahl[]
  aufBerichtigt: () => void
  aufSchliessen: () => void
}) {
  const [ziel, setZiel] = useState(bezug.zielSchluessel)
  const [partner, setPartner] = useState(bezug.partner ?? '')
  const [ab, setAb] = useState<number | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)

  const unveraendert = ziel === bezug.zielSchluessel && partner === (bezug.partner ?? '')
  const davor = ab !== null && ab < bezug.gueltigVon

  async function berichtige() {
    if (ab === null) {
      return
    }

    setLaeuft(true)
    setFehler(null)
    try {
      if (bezug.sorte === 'konto') {
        await api.zuordnen(bezug.einheit, bezug.code, bezug.bezeichnung, ziel, ab, {
          icPartner: partner,
          wirksamAb: ab,
        })
      } else if (bezug.sorte === 'bewegungsart') {
        await api.bewegungsartZuordnen(bezug.einheit, bezug.code, bezug.bezeichnung, ziel, ab, ab)
      } else {
        await api.wertZuordnen(bezug.einheit, bezug.feld ?? '', bezug.code, bezug.bezeichnung, ziel, ab, ab)
      }
      meldeErfolg(t('erfolg.kontenzuordnung.berichtigt'))
      aufBerichtigt()
    } catch (f) {
      setFehler(fachlicherSatz(f))
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <Dialogfenster
      titel={t('mapping.berichtigen.titel', { quelle: bezug.quelle })}
      weite="schmal"
      aufSchliessen={aufSchliessen}
      fuss={
        <>
          <button
            type="button"
            className="haupt"
            disabled={laeuft || ab === null || davor || unveraendert}
            onClick={() => void berichtige()}
          >
            {ab === null
              ? t('mapping.berichtigen.tun.leer')
              : t('mapping.berichtigen.tun', { monat: periodenname(ab) })}
          </button>
          <button type="button" onClick={aufSchliessen}>
            {t('loeschen.zurueck')}
          </button>
        </>
      }
    >
      <label>
        <span className="name">
          {t(
            bezug.sorte === 'konto'
              ? 'mapping.spalte.konzernkonto'
              : bezug.sorte === 'bewegungsart'
                ? 'mapping.spalte.bewegungsart'
                : 'mapping.spalte.ziel',
          )}
        </span>
        <Auswahlfeld wert={ziel} eintraege={zielwahl} beschriftung={bezug.ziel} aufWahl={setZiel} />
      </label>

      {/* **Der Partner nur bei Konten** (R1172, R750): Er ist der feste
          Partner, den eine Kontenzuordnung jeder Lieferung mitgibt. */}
      {bezug.sorte === 'konto' && (
        <label>
          <span className="name">{t('mapping.berichtigen.partner')}</span>
          <Auswahlfeld
            wert={partner}
            eintraege={partnerwahl}
            beschriftung={t('mapping.berichtigen.partner')}
            aufWahl={setPartner}
          />
        </label>
      )}

      <Periodenfeld
        wert={ab}
        aufWert={setAb}
        beschriftung={t('mapping.berichtigen.ab')}
        klasse=""
        erlaeuterung={
          ab === null
            ? undefined
            : davor
              ? t('mapping.berichtigen.davor', { von: periodenname(bezug.gueltigVon) })
              : ab === bezug.gueltigVon
                ? t('mapping.berichtigen.ganz')
                : t('mapping.berichtigen.bis', { monat: periodenname(vormonat(ab)), ziel: bezug.ziel })
        }
      />

      {fehler && <p className="hinweis">{fehler}</p>}
    </Dialogfenster>
  )
}

/**
 * Der Verlauf einer Zuordnung — jede Fassung aller Zeiträume desselben
 * gelieferten Werts, neueste oben (5.1 Abschnitt 3.51, R1175).
 *
 * **Wer, wann, was** — die Fragen eines Prüfers an eine Zuordnung, die
 * entscheidet, wo eine Zahl im Abschluss landet. CoKo hielt jede Fassung mit
 * Zeitpunkt und Anwender fest, zeigte sie bis zum 2026-10-06 aber nirgends.
 */
function Verlaufsfenster({
  bezug,
  zielname,
  partnername,
  aufSchliessen,
}: {
  bezug: Zeilenbezug
  zielname: (schluessel: string) => string
  partnername: (schluessel: string) => string
  aufSchliessen: () => void
}) {
  // Der Verlauf ist der Inhalt des Fensters — ein Hauptabruf (R1211).
  const verlaufabruf = useAbruf(() => api.zuordnungVerlauf(bezug.sorte, bezug.schluessel), [bezug])
  const eintraege = verlaufabruf.wert
  /* **Die Spaltenbreiten zieht der Anwender** (7.3 Abschnitt 10, R415, R833) — wie an jeder Tabelle. */
  const { masse, rahmen } = useSpaltenmasse<HTMLTableElement>('kontenzuordnung:verlauf')

  const mitPartner = bezug.sorte === 'konto'

  return (
    <Dialogfenster
      titel={t('mapping.verlauf.titel', { quelle: bezug.quelle })}
      weite="mittel"
      aufSchliessen={aufSchliessen}
    >
      {verlaufabruf.ausgefallen ? (
        <Abrufausfall abruf={verlaufabruf} />
      ) : eintraege === null ? (
        <p className="hinweis">{t('mapping.verlauf.laedt')}</p>
      ) : (
        <div className="tabellenrahmen">
          <table ref={rahmen} className="ziehbaretabelle verlaufstabelle">
            <Spaltenmassleiste masse={masse} />
            <thead>
              <tr>
                <th>{t('mapping.verlauf.spalte.wann')}</th>
                {/* „Anwender", wie in jedem Protokoll (7.2 Abschnitt 42, R1271) — hier stand „Wer". */}
                <th>{t('protokoll.spalte.anwender')}</th>
                <th>{t('mapping.verlauf.spalte.aenderung')}</th>
                <th>{t('mapping.spalte.ziel')}</th>
                {mitPartner && <th>{t('mapping.berichtigen.partner')}</th>}
                <th>{t('mapping.spalte.gueltig')}</th>
              </tr>
            </thead>
            <tbody>
              {eintraege.map((e, i) => (
                <tr key={i}>
                  <td className="zeitraum">{zeitpunkt(e.zeitpunkt)}</td>
                  {/* Der Name an einer Stelle, auch für den Datenimport selbst (R1273). */}
                  <td>{anwendername(e.anwender)}</td>
                  <td>{t(`mapping.verlauf.tat.${e.tat}` as never)}</td>
                  <td>{zielname(e.ziel)}</td>
                  {mitPartner && <td>{e.partner ? partnername(e.partner) : '—'}</td>}
                  <td className="zeitraum">{zeitraum(e.gueltigVon, e.gueltigBis)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Dialogfenster>
  )
}
